import { useState } from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Tooltip from "@mui/material/Tooltip";
import {
  useManagedUsers,
  useInviteUser,
  useChangeUserRole,
  useDeactivateUser,
  useReactivateUser,
  useDeleteUser,
} from "@/hooks/queries/useUserManagement";
import { useNotification } from "@/hooks/useNotification";
import styles from "./styles.module.css";

const ROLES = [
  { value: "WikiAdmin", label: "Admin" },
  { value: "WikiReader", label: "Reader" },
];

export const UserManagementSection = () => {
  const { data: users = [], isLoading } = useManagedUsers();
  const inviteUser = useInviteUser();
  const changeRole = useChangeUserRole();
  const deactivate = useDeactivateUser();
  const reactivate = useReactivateUser();
  const deleteUserMutation = useDeleteUser();
  const { notifySuccess, notifyError } = useNotification();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("WikiReader");
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; email: string } | null>(null);

  const handleInvite = async () => {
    try {
      const result = await inviteUser.mutateAsync({ email, role });
      if (result.emailSent) {
        notifySuccess(`Invitation email sent to ${email}`);
      } else if (!result.emailConfigured) {
        notifyError("User created but email could not be sent — email service is not configured. Share the sign-up link manually.");
      } else {
        notifyError("User created but the invitation email failed to send. Try deleting and re-inviting.");
      }
      setDialogOpen(false);
      setEmail("");
      setRole("WikiReader");
    } catch {
      notifyError("Failed to send invitation");
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      await changeRole.mutateAsync({ userId, role: newRole });
      notifySuccess("Role updated");
    } catch {
      notifyError("Failed to update role");
    }
  };

  const handleDeactivate = async (userId: string) => {
    try {
      await deactivate.mutateAsync(userId);
      notifySuccess("User deactivated");
    } catch {
      notifyError("Failed to deactivate user");
    }
  };

  const handleReactivate = async (userId: string) => {
    try {
      await reactivate.mutateAsync(userId);
      notifySuccess("User reactivated");
    } catch {
      notifyError("Failed to reactivate user");
    }
  };

  const handleCopyInviteLink = (userEmail: string) => {
    const url = `${window.location.origin}/invite?email=${encodeURIComponent(userEmail)}`;
    navigator.clipboard.writeText(url).then(
      () => notifySuccess("Invite link copied to clipboard"),
      () => notifyError("Failed to copy link"),
    );
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteUserMutation.mutateAsync(deleteTarget.id);
      notifySuccess(`Deleted ${deleteTarget.email}`);
      setDeleteTarget(null);
    } catch {
      notifyError("Failed to delete user");
    }
  };

  if (isLoading) return <div className={styles.section}>Loading...</div>;

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>User Management</h2>
      <Button
        variant="outlined"
        startIcon={<PersonAddIcon />}
        onClick={() => setDialogOpen(true)}
        sx={{ mb: 2 }}
      >
        Invite User
      </Button>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Invited</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.fullName || "—"}</TableCell>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    value={user.role}
                    onChange={(e) => handleRoleChange(user.id, e.target.value)}
                    variant="standard"
                  >
                    {ROLES.map((r) => (
                      <MenuItem key={r.value} value={r.value}>
                        {r.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell>
                  {user.isPending ? (
                    <Chip label="Pending" size="small" color="warning" variant="outlined" />
                  ) : user.isActive ? (
                    <Chip label="Active" size="small" color="success" variant="outlined" />
                  ) : (
                    <Chip label="Inactive" size="small" color="error" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>
                  {user.invitedAt
                    ? new Date(user.invitedAt).toLocaleDateString()
                    : "—"}
                </TableCell>
                <TableCell align="right">
                  {user.isPending && (
                    <Tooltip title="Copy invite link">
                      <IconButton
                        aria-label="copy invite link"
                        size="small"
                        onClick={() => handleCopyInviteLink(user.email)}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {user.isActive ? (
                    <Tooltip title="Deactivate user">
                      <IconButton
                        aria-label="deactivate"
                        size="small"
                        onClick={() => handleDeactivate(user.id)}
                      >
                        <BlockIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Reactivate user">
                      <IconButton
                        aria-label="reactivate"
                        size="small"
                        onClick={() => handleReactivate(user.id)}
                      >
                        <CheckCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Delete user">
                    <IconButton
                      aria-label="delete"
                      size="small"
                      onClick={() => setDeleteTarget({ id: user.id, email: user.email })}
                    >
                      <DeleteOutlineIcon fontSize="small" color="error" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Invite User</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important", minWidth: 360 }}>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            select
            label="Role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            size="small"
          >
            {ROLES.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleInvite}
            variant="contained"
            disabled={!email || inviteUser.isPending}
          >
            Send Invite
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete <strong>{deleteTarget?.email}</strong>? This will remove them from the application and their Entra account. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteUserMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
